import React, { useState } from 'react';

import { path } from 'ramda';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { findIndexFromFieldsArray } from 'components/shared/CRUD/utils/utils';

import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { useEditPageContext } from 'components/shared/CRUD/components/InnerPage/EditPageContext';
import withFieldArrayDrawer from 'components/shared/CRUD/components/InnerPage/components/withFieldArrayDrawer';
import { fieldArrayContext } from 'components/shared/CRUD/components/InnerPage/hooks/useFieldArrayContext';
import useRemoveUncommitted from 'components/shared/CRUD/components/InnerPage/hooks/useRemoveUncommitted';

import { useGetOptions } from '../../../../../../../../../../hooks/useGetOptions';
import { CostCenter } from '../../../../../../../../Procedures/Reports/type';
import WithEditPageLayout, {
  WithEditPageLayoutProps
} from '../../../WithEditPageLayout';
import useColumnDefinitions from '../hooks/useColumnDefinitions';
import EditDataPermission from './EditDataPermission';

interface Props {
  isFetching: boolean;
  costCenters: CostCenter[];
  setDeletedIds: React.Dispatch<any>;
}

const EditDataPermissionDrawer = withFieldArrayDrawer(
  EditDataPermission,
  'costCenterGrants'
);

const DataPermissionsInner: React.FC<WithEditPageLayoutProps & Props> = ({
  isFetching,
  costCenters,
  setDeletedIds
}) => {
  const { t } = useTranslation('common');

  const costCenterOptions = useGetOptions(costCenters, false, 'brId', 'name');
  const columns = useColumnDefinitions(costCenters);

  const { isEditable } = useEditPageContext();

  const { control } = useFormContext();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    fields: costCenterGrantFields,
    append,
    remove,
    update,
    replace
  } = useFieldArray({
    control: control,
    name: 'costCenterGrants'
  });

  const onAddNew = () => {
    // add a new empty record to the fieldArray
    append({ isCommitted: false });
    setIsDrawerOpen(true);
  };

  const onDelete = (fieldIds) => {
    const index = findIndexFromFieldsArray(fieldIds[0], costCenterGrantFields);
    const id = path<number>(['brId'], costCenterGrantFields[index]);
    setDeletedIds((prevState) => [...prevState, id]);

    remove(index);
  };

  useRemoveUncommitted(costCenterGrantFields, isDrawerOpen, remove);

  const onCloseInnerView = () => {
    setIsDrawerOpen(false);
  };

  return (
    <fieldArrayContext.Provider
      value={{ fields: costCenterGrantFields, append, remove, update, replace }}
    >
      <TableCRUDLayout
        FormComponent={(props) => (
          <EditDataPermissionDrawer
            {...props}
            costCenterOptions={costCenterOptions}
          />
        )}
        rows={costCenterGrantFields}
        loading={isFetching}
        columns={columns}
        getRowId={(row) => row.id}
        editable={isEditable}
        confirmationTitle={t('deleteDialog.title')}
        confirmationBody={t('deleteDialog.body')}
        onDeleteClick={onDelete}
        dynamicHeight={true}
        disableActions={!isEditable}
        shouldRenderToolBar={false}
        onAddClick={onAddNew}
        onClose={onCloseInnerView}
      />
    </fieldArrayContext.Provider>
  );
};

const DataPermissionsInnerWithLayout = WithEditPageLayout(DataPermissionsInner);
export default DataPermissionsInnerWithLayout;
